"use client";
import { RefObject, useEffect } from "react";

/**
 * Yatay taşan bir container'ı mouse orta tuşu basılıyken sürükleyerek kaydırır.
 * macOS tarayıcılarında orta tuş autoscroll yok; normal farede (yatay tekerleği olmayan)
 * tabloyu yana kaydırmanın tek yolu buydu. Windows'taki yerleşik autoscroll yerine de bu çalışır.
 */
const useMiddleClickPan = <T extends HTMLElement>(ref: RefObject<T | null>) => {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let pointerId: number | null = null;
    let startX = 0;
    let startScroll = 0;
    let moved = false;

    // Sürükleme yapıldıysa bırakınca link'in yeni sekmede açılmasını / Linux'ta yapıştırmayı engelle
    const onAuxClick = (e: MouseEvent) => {
      if (e.button === 1) e.preventDefault();
    };

    const stop = () => {
      if (pointerId === null) return;
      if (el.hasPointerCapture(pointerId)) el.releasePointerCapture(pointerId);
      pointerId = null;
      el.style.cursor = "";
      document.body.style.userSelect = "";
      if (moved) {
        window.addEventListener("auxclick", onAuxClick, { capture: true, once: true });
        setTimeout(() => window.removeEventListener("auxclick", onAuxClick, { capture: true }), 0);
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return;
      // Tuş pencere dışında (ör. DevTools üstünde) bırakıldıysa pointerup gelmez; burada yakala
      if ((e.buttons & 4) === 0) return stop();
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 3) moved = true;
      el.scrollLeft = startScroll - dx;
    };

    const onPointerUp = (e: PointerEvent) => {
      if (e.pointerId === pointerId) stop();
    };

    const onPointerDown = (e: PointerEvent) => {
      // İç içe tablolarda en içteki kaydırılabilir container işi üstlenir
      if (e.pointerType !== "mouse" || e.button !== 1 || e.defaultPrevented || el.scrollWidth <= el.clientWidth) return;
      e.preventDefault();
      pointerId = e.pointerId;
      startX = e.clientX;
      startScroll = el.scrollLeft;
      moved = false;
      el.setPointerCapture(e.pointerId);
      el.style.cursor = "grabbing";
      document.body.style.userSelect = "none";
    };

    // Windows'taki yerleşik autoscroll'un devreye girmesini engelle
    const onMouseDown = (e: MouseEvent) => {
      if (e.button === 1 && pointerId !== null) e.preventDefault();
    };

    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", onPointerUp);
    el.addEventListener("pointercancel", onPointerUp);
    el.addEventListener("lostpointercapture", onPointerUp);
    el.addEventListener("mousedown", onMouseDown);
    window.addEventListener("blur", stop);
    return () => {
      stop();
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerUp);
      el.removeEventListener("lostpointercapture", onPointerUp);
      el.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("blur", stop);
      window.removeEventListener("auxclick", onAuxClick, { capture: true });
    };
  }, [ref]);
};

export default useMiddleClickPan;
